import Alert from '@mui/material/Alert'
import { useEffect } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { I18n } from '../../../../components/I18n.tsx'
import { Icon } from '../../../../components/Icon.tsx'
import { NumberInput } from '../../../../components/NumberInput.tsx'
import type { AssetLocation } from '../../../../data/locations.tsx'
import { ids } from '../../../../ids.ts'
import { useDistanceRangeHelpers } from '../../../distance-range-helpers.ts'
import { coversArea } from '../buffer/bufferReportGeometry.ts'
import { type BaseReportForm, useBaseFormValues } from '../schema.ts'

export const RadiusField = ({
	selectedLocations,
	maxRadiusKm,
	maxAssets,
}: {
	selectedLocations: AssetLocation[]
	maxRadiusKm: number
	maxAssets: number
}) => {
	const { t } = useTranslation()
	const { setValue } = useFormContext<BaseReportForm>()
	const [radiusKm, customPoint, geometryTooComplex] = useBaseFormValues(['radiusKm', 'customPoint', 'geometryTooComplex'])
	const geometries = customPoint ? [customPoint] : selectedLocations.map((location) => location.geometry)
	const radiusHelpers = useDistanceRangeHelpers(geometries.some((geometry) => !coversArea(geometry)) ? 1 : 0, maxRadiusKm)
	const minRadiusKm = radiusHelpers.fromUserPreferenceToKm(radiusHelpers.minDistanceInUserPreferenceUnit)

	useEffect(() => {
		if (radiusKm < minRadiusKm) setValue('radiusKm', minRadiusKm)
	}, [radiusKm, minRadiusKm])

	return (
		<>
			<NumberInput
				dataTa={ids.reports.form.areaOfInterest.radius}
				endSymbol={radiusHelpers.formattedUnit}
				label={<I18n i18nKey={($) => $.reports.form.radius} />}
				required
				decimals={2}
				value={radiusHelpers.fromKmToUserPreference(radiusKm, 2)}
				onChange={(e) => setValue('radiusKm', radiusHelpers.fromUserPreferenceToKm(e.validatedValue))}
				onChangeCommited={(value) => setValue('radiusKm', radiusHelpers.fromUserPreferenceToKm(value))}
				min={radiusHelpers.minDistanceInUserPreferenceUnit}
				max={radiusHelpers.maxDistanceInUserPreferenceUnit}
				helperText={[
					`${radiusHelpers.minDistanceInUserPreferenceUnit}-${radiusHelpers.maxDistanceInUserPreferenceUnit} ${radiusHelpers.formattedUnit}`,
					maxAssets > 1 && t(($) => $.reports.form.radiusAppliedToEverySelectedAsset),
				]
					.filter(Boolean)
					.join(' · ')}
				disabled={selectedLocations.length === 0}
			/>
			{geometryTooComplex && (
				<Alert icon={<Icon name='CircleExclamation' />} severity='error'>
					<I18n i18nKey={($) => $.reports.form.geometryTooComplex} />
				</Alert>
			)}
		</>
	)
}
